import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import type { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { ToastrService } from 'ngx-toastr';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';
import { finalize, catchError, of, merge } from 'rxjs';
import { PlanService } from '../../services/plan.service';
import { CurrencyService } from '../../services/currency.service';
import { ProductService } from '../../services/product.service';
import { MeasureUnitService } from '../../services/measure-unit.service';
import { TaxProductCodeService } from '../../services/tax-product-code.service';
import { TaxService } from '../../services/tax.service';
import { SATObjetoImpService } from '../../services/sat-objeto-imp.service';
import {
  PlanStatusEnum,
  toLineTaxApplicationTypeForCreate
} from '../../core/models';
import type {
  PlanDTO,
  PlanItemDTO,
  CreatePlanRequest,
  CreateSaleLineRequest,
  CreateSaleLineTaxRequest,
  PreviewSaleLineRequest,
  CurrencyDTO,
  ProductDTO,
  MeasureUnitDTO,
  TaxProductCodeDTO,
  TaxDTO,
  SATObjetoImpDTO,
  FrequencyEnumValue,
  PlanStatusEnumValue,
  TaxApplicationTypeEnumValue
} from '../../core/models';

export interface PlanItemVm {
  tempId?: string;
  id?: number;
  productId?: number;
  product?: ProductDTO;
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  measureUnitId?: number;
  measureUnit?: MeasureUnitDTO;
  taxProductCodeId?: number;
  taxProductCode?: TaxProductCodeDTO;
  satObjetoImpId?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxes: CreateSaleLineTaxRequest[];
}

@Component({
  selector: 'app-plan-form',
  templateUrl: './plan-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FloatLabelModule,
    SelectModule,
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    TabsModule,
    TooltipModule,
    CheckboxModule,
    SelectButtonModule,
    EntityAuditPanelComponent
  ]
})
export class PlanFormComponent {
  private fb = inject(FormBuilder);
  private planService = inject(PlanService);
  private currencyService = inject(CurrencyService);
  private productService = inject(ProductService);
  private measureUnitService = inject(MeasureUnitService);
  private taxProductCodeService = inject(TaxProductCodeService);
  private taxService = inject(TaxService);
  private satObjetoImpService = inject(SATObjetoImpService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  planForm: FormGroup;
  loading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingPlanId = signal<number | null>(null);
  currentPlan = signal<PlanDTO | null>(null);
  activeTab = signal<string>('general');

  items = signal<PlanItemVm[]>([]);

  lineModalOpen = signal(false);
  lineModalShown = signal(false);
  lineModalMode = signal<'create' | 'edit'>('create');
  /** Datos fiscales e impuestos del diálogo de item: ocultos por defecto. */
  lineFiscalDetailsExpanded = signal(false);
  editingLineIndex = signal<number | null>(null);
  lineForm: FormGroup;
  lineSaving = signal(false);
  lineRemoving = signal(false);
  /** Subtotal calculado en el modal de línea (cantidad × precio). */
  lineSubtotal = signal(0);

  productOptions = signal<ProductDTO[]>([]);
  currencyOptions = signal<CurrencyDTO[]>([]);
  measureUnitOptions = signal<MeasureUnitDTO[]>([]);
  taxProductCodeOptions = signal<TaxProductCodeDTO[]>([]);
  satObjetoImpOptions = signal<SATObjetoImpDTO[]>([]);
  taxOptions = signal<TaxDTO[]>([]);

  lineTaxModalOpen = signal(false);
  lineTaxForm: FormGroup;
  editingLineTaxIndex = signal<number | null>(null);
  lineFormTaxes = signal<CreateSaleLineTaxRequest[]>([]);

  expandedLineIndices = signal<Set<number>>(new Set());

  lineProductSource = signal<'CATALOG' | 'OPEN'>('CATALOG');
  lineProductSourceOptions: { label: string; value: 'CATALOG' | 'OPEN' }[] = [
    { label: 'Producto de catálogo', value: 'CATALOG' },
    { label: 'Producto abierto', value: 'OPEN' }
  ];

  readonly lineProductSourceTooltip =
    '<strong>Producto de catálogo:</strong> eliges un artículo del inventario; se precargan descripción, precio, unidad, impuestos y datos fiscales del producto.<br><br>' +
    '<strong>Producto abierto:</strong> escribes la descripción y montos a mano, sin vincular un SKU.';

  statusOptions: { value: PlanStatusEnumValue; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'ARCHIVED', label: 'Archivado' }
  ];

  frequencyOptions: { value: FrequencyEnumValue; label: string }[] = [
    { value: 'ONCE', label: 'Una vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  currencySelectOptions = computed(() =>
    this.currencyOptions().map((c) => ({ id: c.id, label: `${c.code} - ${c.name}` }))
  );

  taxExemptOptions = [
    { label: 'No', value: false },
    { label: 'Sí', value: true }
  ];

  taxApplicationOptions = [
    { label: 'Gravado', value: 'TAXABLE' },
    { label: 'Exento', value: 'EXEMPT' },
    { label: 'Tasa cero', value: 'ZERO_RATED' }
  ];

  taxCollectionTypeOptions = [
    { label: 'Traslado', value: 'SELLER_COLLECTS' },
    { label: 'Retención', value: 'SELLER_WITHHOLDS' }
  ];

  readonly taxInclusiveHelpText = 'Si está marcado, el precio unitario ya incluye impuestos y el motor fiscal calculará la base gravable.';

  totals = computed(() => {
    const ls = this.items();
    let subtotal = 0;
    let tax = 0;
    let traslados = 0;
    let retenciones = 0;
    for (const l of ls) {
      subtotal += l.subtotal;
      for (const t of l.taxes) {
        const amt = Number(t.taxAmount) || 0;
        if (t.collectionType === 'SELLER_WITHHOLDS') {
          retenciones += Math.abs(amt);
        } else {
          traslados += amt;
        }
      }
    }
    tax = traslados - retenciones;
    const discount = Number(this.planForm?.get('discount')?.value ?? 0);
    const total = subtotal + tax - discount;
    return { subtotal, tax, traslados, retenciones, total };
  });

  get currencyCode(): string {
    const id = this.planForm.get('currencyId')?.value;
    if (!id) return 'MXN';
    const c = this.currencyOptions().find((x) => x.id === id);
    return c?.code || 'MXN';
  }

  private effectTotals = effect(() => {
    const t = this.totals();
    this.planForm.patchValue(
      { subtotal: t.subtotal, tax: t.tax, total: t.total },
      { emitEvent: false }
    );
  });

  constructor() {
    this.planForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      description: ['', [Validators.maxLength(500)]],
      currencyId: [null, [Validators.required]],
      frequency: ['MONTHLY', [Validators.required]],
      status: ['DRAFT', [Validators.required]],
      discount: [0],
      subtotal: [0],
      tax: [0],
      total: [0]
    });

    this.lineForm = this.fb.group({
      productSelection: [null],
      productId: [null],
      description: ['', [Validators.required, Validators.maxLength(250)]],
      quantity: [1, [Validators.required, Validators.min(0.0001)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      measureUnitSelection: [null],
      measureUnitId: [null],
      taxProductCodeSelection: [null],
      taxProductCodeId: [null],
      satObjetoImpId: [null],
      taxExempt: [false],
      taxInclusive: [false]
    });

    merge(
      this.lineForm.get('quantity')!.valueChanges,
      this.lineForm.get('unitPrice')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshLineSubtotal());
    this.refreshLineSubtotal();

    this.lineTaxForm = this.fb.group({
      taxSelection: [null, [Validators.required]],
      taxRate: [0],
      taxAmount: [0],
      taxBase: [0],
      applicationType: ['TAXABLE'],
      collectionType: ['SELLER_COLLECTS']
    });

    const paramId = this.route.snapshot.paramMap.get('id');
    if (paramId && paramId !== 'nuevo') {
      this.isEditMode.set(true);
      this.editingPlanId.set(Number(paramId));
      this.loadPlan(Number(paramId));
    }

    this.loadCurrencies();
    this.loadTaxes();
    this.loadSatObjetoImps();
  }

  cancel(): void {
    this.router.navigate(['/plans']);
  }

  readonly statusTooltip = 'Estado del plan: Borrador, Activo o Archivado.';

  statusDisplayLabel(): string {
    const status = this.planForm.get('status')?.value;
    return this.statusOptions.find((o) => o.value === status)?.label ?? (status || '—');
  }

  // ── Carga de catálogos ──

  loadCurrencies(): void {
    this.currencyService
      .listCurrencies({}, { page: 0, size: 200 })
      .pipe(catchError(() => of({ ok: false, body: [] as CurrencyDTO[] })))
      .subscribe((r) => {
        this.currencyOptions.set(r.ok ? (r.body ?? []) : []);
        this.applyDefaultCurrencyIfNeeded();
      });
  }

  private applyDefaultCurrencyIfNeeded(): void {
    if (this.editingPlanId() != null) return;
    if (this.planForm.get('currencyId')?.value != null) return;
    const mxn = this.currencyOptions().find((c) => String(c.code).toUpperCase() === 'MXN');
    if (mxn?.id) this.planForm.patchValue({ currencyId: mxn.id }, { emitEvent: false });
  }

  loadTaxes(): void {
    this.taxService
      .listTaxes({}, { page: 0, size: 200 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxDTO[] })))
      .subscribe((r) => this.taxOptions.set(r.ok ? (r.body ?? []) : []));
  }

  private loadSatObjetoImps(page = 0, acc: SATObjetoImpDTO[] = []): void {
    this.satObjetoImpService
      .list({}, { page, size: 200, sort: ['name,asc'] })
      .pipe(catchError(() => of({ ok: false, body: [] as SATObjetoImpDTO[], page: undefined })))
      .subscribe((response: any) => {
        if (!response.ok) { this.satObjetoImpOptions.set([]); return; }
        const merged = [...acc, ...(response.body ?? [])];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadSatObjetoImps(response.page.currentPage + 1, merged);
          return;
        }
        this.satObjetoImpOptions.set(merged);
      });
  }

  // ── Carga y guardado del plan ──

  loadPlan(id: number): void {
    this.loading.set(true);
    this.planService.getPlanById(id).subscribe({
      next: (r) => {
        if (r.ok && r.body) {
          this.currentPlan.set(r.body);
          this.patchPlan(r.body);
          this.items.set((r.body.items ?? []).map((i) => this.itemToVm(i)));
        } else {
          this.error.set(r.message || 'Error al cargar el plan');
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Error al cargar el plan.');
        this.loading.set(false);
      }
    });
  }

  private itemToVm(item: PlanItemDTO): PlanItemVm {
    const taxes: CreateSaleLineTaxRequest[] = (item.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate,
      taxAmount: t.taxAmount,
      taxBase: t.taxBase,
      applicationType: toLineTaxApplicationTypeForCreate(t.applicationType as TaxApplicationTypeEnumValue),
      collectionType: t.collectionType as 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS' | undefined
    }));
    return {
      id: item.id,
      productId: item.product?.id,
      product: item.product,
      description: item.description ?? '',
      quantity: Number(item.quantity ?? 0),
      unitPrice: Number(item.unitPrice ?? 0),
      subtotal: Number(item.subtotal ?? 0),
      taxAmount: Number(item.taxAmount ?? 0),
      totalAmount: Number(item.totalAmount ?? 0),
      measureUnitId: item.measureUnit?.id,
      measureUnit: item.measureUnit,
      taxProductCodeId: item.taxProductCode?.id,
      taxProductCode: item.taxProductCode,
      satObjetoImpId: item.satObjetoImp?.id,
      satObjetoImp: item.satObjetoImp,
      taxExempt: item.taxExempt,
      taxInclusive: item.taxInclusive,
      taxes
    };
  }

  patchPlan(plan: PlanDTO): void {
    this.planForm.patchValue({
      name: plan.name ?? '',
      description: plan.description ?? '',
      frequency: plan.frequency ?? 'MONTHLY',
      status: plan.status ?? 'DRAFT',
      discount: plan.discount ?? 0,
      subtotal: plan.subtotal ?? 0,
      tax: plan.tax ?? 0,
      total: plan.total ?? 0,
      currencyId: null
    });
    if (plan.currency && typeof plan.currency === 'string') {
      const cur = this.currencyOptions().find((c) => c.code === plan.currency);
      if (cur) this.planForm.patchValue({ currencyId: cur.id });
    }
    this.cdr.markForCheck();
  }

  onSubmit(): void {
    if (this.planForm.invalid) {
      this.planForm.markAllAsTouched();
      this.toastr.error('Completa los campos obligatorios.');
      return;
    }

    this.loading.set(true);
    const req = this.buildRequest();
    const isEdit = this.isEditMode() && this.editingPlanId();
    const req$ = isEdit
      ? this.planService.updatePlan(this.editingPlanId()!, req)
      : this.planService.createPlan(req);

    req$.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (r) => {
        if (r.ok && r.body) {
          const id = r.body.id;
          if (id) {
            this.isEditMode.set(true);
            this.editingPlanId.set(id);
            this.currentPlan.set(r.body);
            this.patchPlan(r.body);
            this.items.set((r.body.items ?? []).map((i) => this.itemToVm(i)));
            this.router.navigate(['/plans', id], { replaceUrl: true });
          }
          this.toastr.success(isEdit ? 'Plan actualizado' : 'Plan creado');
        } else {
          this.error.set(r.message || 'Error al guardar el plan');
        }
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Error al conectar con el servidor');
      }
    });
  }

  private buildRequest(): CreatePlanRequest {
    const v = this.planForm.getRawValue();
    const t = this.totals();
    const lines: CreateSaleLineRequest[] = this.items().map((l) => {
      const subtotal = Number(l.quantity) * Number(l.unitPrice);
      const taxAmount = l.taxes?.reduce((s, tx) => s + (Number(tx.taxAmount) || 0), 0) ?? 0;
      return {
        id: l.id,
        productId: l.productId,
        description: l.description,
        measureUnitId: l.measureUnitId,
        taxProductCodeId: l.taxProductCodeId,
        satObjetoImpId: l.satObjetoImpId,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        subtotal: Number(l.subtotal) || subtotal,
        taxAmount: Math.max(0, Number(l.taxAmount) ?? taxAmount),
        totalAmount: Math.max(0, Number(l.totalAmount) ?? (subtotal + taxAmount)),
        taxExempt: l.taxExempt,
        taxInclusive: l.taxInclusive,
        taxes: l.taxes?.length ? l.taxes : undefined
      };
    });

    return {
      name: v.name,
      description: v.description || undefined,
      currencyId: Number(v.currencyId),
      frequency: v.frequency,
      status: v.status,
      discount: Number(v.discount ?? 0),
      items: lines.length ? lines : undefined
    };
  }

  // ── Búsquedas de catálogos ──

  onProductSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) { this.productOptions.set([]); return; }
    this.productService
      .getProducts({ 'name[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as ProductDTO[] })))
      .subscribe((r) => this.productOptions.set(r.ok && r.body ? (r.body as ProductDTO[]) : []));
  }

  onMeasureUnitSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) { this.measureUnitOptions.set([]); return; }
    this.measureUnitService
      .listMeasureUnits({ 'name[lk]': q, 'code[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as MeasureUnitDTO[] })))
      .subscribe((r) => this.measureUnitOptions.set(r.ok && r.body ? (r.body as MeasureUnitDTO[]) : []));
  }

  /** Subtotal del modal de línea: cantidad × precio unitario. */
  private refreshLineSubtotal(): void {
    const qty = Number(this.lineForm.get('quantity')?.value) || 0;
    const price = Number(this.lineForm.get('unitPrice')?.value) || 0;
    this.lineSubtotal.set(qty * price);
  }

  /**
   * Selecciona todo el texto al enfocar cantidad/precio (Tab o click).
   * El flag + mouseup evitan que el click reposicione el caret; setTimeout
   * cubre el caso en que el mouseup ya haya corrido en el input interno.
   */
  private suppressMouseUpSelectClear = false;

  selectAllOnFocus(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input?.select) {
      return;
    }
    this.suppressMouseUpSelectClear = true;
    input.select();
    setTimeout(() => input.select());
  }

  /** Evita que el mouseup del click deshaga la selección hecha en focus. */
  keepSelectionOnMouseUp(event: Event): void {
    if (!this.suppressMouseUpSelectClear) {
      return;
    }
    event.preventDefault();
    this.suppressMouseUpSelectClear = false;
  }

  onTaxProductCodeSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) { this.taxProductCodeOptions.set([]); return; }
    this.taxProductCodeService
      .listTaxProductCodes({ 'taxCode[lk]': q, 'taxDescription[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxProductCodeDTO[] })))
      .subscribe((r) => this.taxProductCodeOptions.set(r.ok && r.body ? (r.body as TaxProductCodeDTO[]) : []));
  }

  onMeasureUnitSelected(value: MeasureUnitDTO | null): void {
    if (value) this.lineForm.patchValue({ measureUnitId: value.id ?? null, measureUnitSelection: value });
  }

  onTaxProductCodeSelected(value: TaxProductCodeDTO | null): void {
    if (value) this.lineForm.patchValue({ taxProductCodeId: value.id ?? null, taxProductCodeSelection: value });
  }

  onProductSelected(p: ProductDTO | null): void {
    if (!p) return;
    const satObjetoImp = p.satObjetoImpId
      ? this.satObjetoImpOptions().find((s) => s.id === p.satObjetoImpId) ?? { id: p.satObjetoImpId, name: p.satObjetoImpName ?? p.satObjetoImpId }
      : null;
    this.lineForm.patchValue({
      productId: p.id,
      productSelection: p,
      description: p.name ?? this.lineForm.get('description')?.value,
      unitPrice: p.unitPrice ?? 0,
      measureUnitSelection: p.measureUnitId ? { id: p.measureUnitId, code: p.measureUnitCode ?? '', name: p.measureUnitName ?? '' } : null,
      measureUnitId: p.measureUnitId,
      taxProductCodeSelection: p.taxCodeId ? { id: p.taxCodeId, taxCode: p.taxCodeTaxCode ?? '', taxDescription: p.taxCodeDescription ?? '' } : null,
      taxProductCodeId: p.taxCodeId,
      satObjetoImpId: p.satObjetoImpId ?? null
    });
    const taxes: CreateSaleLineTaxRequest[] = (p.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate ?? t.tax?.rate,
      taxAmount: t.taxAmount,
      taxBase: 0,
      applicationType: toLineTaxApplicationTypeForCreate((t.applicationType ?? 'TAXABLE') as TaxApplicationTypeEnumValue),
      collectionType: 'SELLER_COLLECTS' as const
    }));
    this.lineFormTaxes.set(taxes);
    this.editingLineTaxIndex.set(null);
  }

  // ── Modal de línea ──

  openLineModal(mode: 'create' | 'edit', index?: number): void {
    this.lineModalMode.set(mode);
    this.editingLineIndex.set(index ?? null);
    this.lineFiscalDetailsExpanded.set(false);
    this.lineProductSource.set('CATALOG');

    if (mode === 'edit' && index != null) {
      const l = this.items()[index];
      this.lineForm.patchValue({
        productSelection: l.product ?? null,
        productId: l.productId ?? null,
        description: l.description,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        measureUnitSelection: l.measureUnit ?? null,
        measureUnitId: l.measureUnitId ?? null,
        taxProductCodeSelection: l.taxProductCode ?? null,
        taxProductCodeId: l.taxProductCodeId ?? null,
        satObjetoImpId: l.satObjetoImpId ?? null,
        taxExempt: l.taxExempt ?? false,
        taxInclusive: l.taxInclusive ?? false
      });
      this.lineFormTaxes.set([...l.taxes]);
      this.lineProductSource.set(l.productId ? 'CATALOG' : 'OPEN');
    } else {
      this.lineForm.reset({
        productSelection: null, productId: null,
        description: '', quantity: 1, unitPrice: 0,
        measureUnitSelection: null, measureUnitId: null,
        taxProductCodeSelection: null, taxProductCodeId: null,
        satObjetoImpId: null, taxExempt: false, taxInclusive: false
      });
      this.lineFormTaxes.set([]);
    }

    this.lineModalOpen.set(true);
    setTimeout(() => this.lineModalShown.set(true), 10);
  }

  closeLineModal(): void {
    this.lineModalShown.set(false);
    setTimeout(() => {
      this.lineModalOpen.set(false);
      this.editingLineIndex.set(null);
    }, 200);
  }

  saveLine(): void {
    if (this.lineForm.invalid) return;

    const v = this.lineForm.getRawValue();
    const qty = Number(v.quantity) ?? 1;
    const unitPrice = Number(v.unitPrice) ?? 0;
    const subtotalInput = qty * unitPrice;
    const taxesInput = this.lineFormTaxes();

    const previewRequest: PreviewSaleLineRequest = {
      subtotal: subtotalInput,
      taxInclusive: v.taxInclusive ?? false,
      taxes: (taxesInput ?? []).map((t) => ({
        taxCode: t.taxCode,
        taxRate: t.taxRate,
        taxAmount: t.taxAmount,
        taxBase: t.taxBase,
        applicationType: t.applicationType,
        collectionType: t.collectionType
      }))
    };

    this.lineSaving.set(true);
    this.planService
      .previewPlanItem(previewRequest)
      .pipe(finalize(() => this.lineSaving.set(false)))
      .subscribe({
        next: (res) => {
          if (!res.ok || !res.body) {
            this.setError(res.message || 'No se pudo calcular el preview de la línea.');
            return;
          }
          const lineDto = res.body;
          const taxesFromPreview: CreateSaleLineTaxRequest[] = (lineDto.taxes ?? []).map((t, i) => {
            const inputTax = taxesInput[i];
            return {
              taxCode: t.tax?.taxKey,
              taxRate: t.taxRate,
              taxAmount: t.taxAmount,
              taxBase: t.taxBase,
              applicationType: toLineTaxApplicationTypeForCreate(
                (t.applicationType as TaxApplicationTypeEnumValue) ?? 'TAXABLE'
              ),
              collectionType: (inputTax?.collectionType ?? t.collectionType ?? 'SELLER_COLLECTS') as
                | 'SELLER_COLLECTS'
                | 'SELLER_WITHHOLDS'
            };
          });

          const satObjetoImp = v.satObjetoImpId
            ? this.satObjetoImpOptions().find((s) => s.id === v.satObjetoImpId) ?? { id: v.satObjetoImpId, name: '' }
            : undefined;

          const vm: PlanItemVm = {
            tempId: this.editingLineIndex() != null
              ? this.items()[this.editingLineIndex()!].tempId
              : crypto.randomUUID(),
            id: this.editingLineIndex() != null
              ? this.items()[this.editingLineIndex()!].id
              : undefined,
            productId: v.productId ?? undefined,
            product: v.productSelection ?? undefined,
            description: v.description,
            quantity: qty,
            unitPrice,
            subtotal: Number(lineDto.subtotal ?? subtotalInput),
            taxAmount: Number(lineDto.taxAmount ?? 0),
            totalAmount: Number(lineDto.totalAmount ?? subtotalInput),
            measureUnitId: v.measureUnitId ?? undefined,
            measureUnit: v.measureUnitSelection ?? undefined,
            taxProductCodeId: v.taxProductCodeId ?? undefined,
            taxProductCode: v.taxProductCodeSelection ?? undefined,
            satObjetoImpId: v.satObjetoImpId ?? undefined,
            satObjetoImp: satObjetoImp,
            taxExempt: v.taxExempt ?? false,
            taxInclusive: v.taxInclusive ?? false,
            taxes: taxesFromPreview
          };

          if (this.editingLineIndex() != null) {
            this.items.update((arr) => arr.map((item, i) => i === this.editingLineIndex() ? vm : item));
          } else {
            this.items.update((arr) => [...arr, vm]);
          }

          const t = this.totals();
          this.planForm.patchValue(
            { subtotal: t.subtotal, tax: t.tax, discount: this.planForm.get('discount')?.value ?? 0, total: t.total },
            { emitEvent: false }
          );
          this.planForm.updateValueAndValidity({ emitEvent: false });
          this.closeLineModal();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al calcular impuestos de la línea.');
        }
      });
  }

  confirmRemoveLine(index: number): void {
    this.items.update((arr) => arr.filter((_, i) => i !== index));
  }

  toggleLineExpanded(index: number): void {
    this.expandedLineIndices.update((set) => {
      const next = new Set(set);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }

  isLineExpanded(index: number): boolean {
    return this.expandedLineIndices().has(index);
  }

  // ── Modal de impuesto ──

  editLineTaxInModal(index: number | null): void {
    this.editingLineTaxIndex.set(index);
    if (index != null) {
      const t = this.lineFormTaxes()[index];
      const match = this.taxOptions().find((o) => o.taxKey === t.taxCode);
      this.lineTaxForm.patchValue({
        taxSelection: match ?? null,
        taxRate: t.taxRate ?? 0,
        taxAmount: t.taxAmount ?? 0,
        taxBase: t.taxBase ?? 0,
        applicationType: t.applicationType ?? 'TAXABLE',
        collectionType: t.collectionType ?? 'SELLER_COLLECTS'
      });
    } else {
      this.lineTaxForm.reset({
        taxSelection: null, taxRate: 0, taxAmount: 0, taxBase: 0,
        applicationType: 'TAXABLE', collectionType: 'SELLER_COLLECTS'
      });
    }
    this.lineTaxModalOpen.set(true);
  }

  closeLineTaxModal(): void {
    this.lineTaxModalOpen.set(false);
    this.editingLineTaxIndex.set(null);
  }

  saveLineTaxInModal(): void {
    if (this.lineTaxForm.invalid) return;
    const v = this.lineTaxForm.getRawValue();
    const tax: CreateSaleLineTaxRequest = {
      taxCode: v.taxSelection?.taxKey,
      taxRate: v.taxRate,
      taxAmount: v.taxAmount,
      taxBase: v.taxBase,
      applicationType: v.applicationType,
      collectionType: v.collectionType
    };
    if (this.editingLineTaxIndex() != null) {
      this.lineFormTaxes.update((arr) => arr.map((t, i) => i === this.editingLineTaxIndex() ? tax : t));
    } else {
      this.lineFormTaxes.update((arr) => [...arr, tax]);
    }
    this.closeLineTaxModal();
  }

  removeLineTaxInModal(index: number): void {
    this.lineFormTaxes.update((arr) => arr.filter((_, i) => i !== index));
  }

  // ── Helpers ──

  onLineProductSourceSelectChange(ev: SelectButtonChangeEvent): void {
    const v = ev.value as 'CATALOG' | 'OPEN' | null;
    if (v !== 'CATALOG' && v !== 'OPEN') return;
    this.lineProductSource.set(v);
    if (v === 'OPEN') {
      this.lineForm.patchValue({ productSelection: null, productId: null });
      this.lineFormTaxes.set([]);
    }
  }

  getTaxDisplayWithCode(code: string | undefined): string {
    if (!code) return '—';
    const match = this.taxOptions().find((t) => t.taxKey === code);
    return match ? `${match.displayName} (${code})` : code;
  }

  getTaxApplicationLabel(v: string): string {
    return this.taxApplicationOptions.find((o) => o.value === v)?.label ?? v;
  }

  getTaxCollectionTypeLabel(v: string | undefined): string {
    if (!v) return '—';
    return this.taxCollectionTypeOptions.find((o) => o.value === v)?.label ?? v;
  }

  formatTaxRate(rate: number | undefined | null): string {
    if (rate == null) return '-';
    return `${rate}%`;
  }

  private setError(msg: string): void {
    this.error.set(msg);
    this.toastr.error(msg);
  }
}
